import React, { useState } from 'react';
import { Grid, Box, Input } from '@chakra-ui/react';
import { Res, TicketLineDTO } from 'core/api';
import { Currency } from 'core/utils';
import * as Images from 'pos/Images';
import { Format } from 'core/ui';
import { Math } from 'core/utils';
import { Command } from 'pos/common';
import { NumericButton, FunctionButton } from 'pos/ui/Keypad.Button';

const format = Format.asDecimalWith(Currency.DECIMAL_PLACES);

export type KeypadRef = {
	clear: () => void;
	getRawValue: () => string;
	getValue: () => number | undefined;
}

type Props = {
	ref?: React.Ref<KeypadRef>,
	line: Nullable<TicketLineDTO>
};

const Keypad: React.FC<Props> = ({
	ref,
	line
}) => {
	const [value, setValue] = useState('');

	const getValue = React.useCallback(() => {
		const normalized = value.replace(',', '.');
		const numberValue = normalized === '' ? NaN : Number(normalized);
		if (isNaN(numberValue))
			return undefined;

		return numberValue;
	}, [value]);

	const isEmpty = React.useCallback(() => value === '', [value]);

	const sendInput = (content: string) => {
		if ((content === '.' || content === ',') && value.includes(',')) return;

		const newValue = value + content;
		const replaced = newValue.replace('.', Currency.DECIMAL_SEPARATOR);

		if (replaced.split(Currency.DECIMAL_SEPARATOR)[0].length > 6) return;

		setValue(format(replaced));
	};

	const inverseInput = React.useCallback(() => {
		const newValue = value.startsWith('-') ? value.slice(1) : '-' + value;
		setValue(format(newValue));
	}, [setValue, value]);

	const clearInput = React.useCallback(() => setValue(''), [setValue]);

	const buildFnHandler = React.useCallback((fn: (val: number, line: TicketLineDTO) => void) => () => {

		if (isEmpty())
			return;

		if (line === null)
			return;

		const value = getValue();
		if (value !== undefined)
			fn(value, line);

	}, [line, getValue, isEmpty]);

	const onApplyLineDiscountPercentage = buildFnHandler(async (discountPercentage: number, line: TicketLineDTO) => {
		await Command.execute('setTicketLineDiscountPercentage', line, Math.round(discountPercentage, Currency.DECIMAL_PLACES));
		clearInput();
	});

	const onApplyLineCashDiscount = buildFnHandler(async (cashDiscount: number, line: TicketLineDTO) => {
		await Command.execute('setTicketLineCashDiscount', line, Math.round(cashDiscount, Currency.DECIMAL_PLACES));
		clearInput();
	});

	const onApplyLineQuantity = buildFnHandler(async (quantity: number, line: TicketLineDTO) => {
		await Command.execute('setTicketLineQuantity', line, quantity);
		clearInput();
	});

	const onApplyLinePrice = buildFnHandler(async (price: number, line: TicketLineDTO) => {
		await Command.execute('setTicketLinePrice', line, Math.round(price, Currency.DECIMAL_PLACES));
		clearInput();
	});

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const { key } = e;

		if (/^[0-9]$/.test(key)) {
			e.preventDefault();
			sendInput(key);
			return;
		}

		if (key === '.' || key === ',') {
			e.preventDefault();
			sendInput('.');
			return;
		}

		if (key === '-') {
			e.preventDefault();
			inverseInput();
			return;
		}

		if (key === 'Backspace') {
			e.preventDefault();
			setValue(value.slice(0, -1));
			return;
		}

		if (key === 'Delete' || key === 'Escape') {
			e.preventDefault();
			clearInput();
		}
	};


	// Exponemos las funciones que queremos usar desde fuera en el tipo del ref
	React.useImperativeHandle(ref, () => ({
		getRawValue: () => value,
		getValue,
		clear: clearInput,
	}));

	return (
		<Grid templateColumns='1fr 1fr 1fr 4.5rem' templateRows='repeat(5, 3rem)' gap={2} w='full'>
			<Box gridRow={1} gridColumn={'1 / span 3'}>
				<Input name={'sale-keypad-input'} placeholder={'0'} size='md' value={value} onChange={() => { }} onKeyDown={onKeyDown} textAlign='right' height={'full'} bg={{ base: 'white', _dark: '{colors.neutral.800}' }} fontSize={'xl'} fontWeight={'semibold'} />
			</Box>

			<Box gridRow={2} gridColumn={1}><NumericButton button='7' handleButton={sendInput} /></Box>
			<Box gridRow={2} gridColumn={2}><NumericButton button='8' handleButton={sendInput} /></Box>
			<Box gridRow={2} gridColumn={3}><NumericButton button='9' handleButton={sendInput} /></Box>

			<Box gridRow={3} gridColumn={1}><NumericButton button='4' handleButton={sendInput} /></Box>
			<Box gridRow={3} gridColumn={2}><NumericButton button='5' handleButton={sendInput} /></Box>
			<Box gridRow={3} gridColumn={3}><NumericButton button='6' handleButton={sendInput} /></Box>

			<Box gridRow={4} gridColumn={1}><NumericButton button='1' handleButton={sendInput} /></Box>
			<Box gridRow={4} gridColumn={2}><NumericButton button='2' handleButton={sendInput} /></Box>
			<Box gridRow={4} gridColumn={3}><NumericButton button='3' handleButton={sendInput} /></Box>

			<Box gridRow={5} gridColumn={1}><NumericButton button='0' handleButton={sendInput} /></Box>
			<Box gridRow={5} gridColumn={2}><NumericButton button={Currency.DECIMAL_SEPARATOR} handleButton={sendInput} /></Box>
			<Box gridRow={5} gridColumn={3}><NumericButton button='-' handleButton={inverseInput} /></Box>

			<Box gridRow={1} gridColumn={4}>
				<FunctionButton handleButton={clearInput} src={Images.ClearCalculator} />
			</Box>
			<Box gridRow={2} gridColumn={4}>
				<FunctionButton text={Res.Web.WEB_KEYPAD_DISPLAY_TICKET_CONTROL_DISCOUNT_BTN()} src={Images.Discount} direction={'column'} handleButton={onApplyLineDiscountPercentage} />
			</Box>
			<Box gridRow={3} gridColumn={4}>
				<FunctionButton text={Res.Web.WEB_KEYPAD_DISPLAY_TICKET_CONTROL_DISCOUNT_BTN()} currencySymbol={Currency.SYMBOL} direction={'column'} handleButton={onApplyLineCashDiscount} />
			</Box>
			<Box gridRow={4} gridColumn={4}>
				<FunctionButton text={Res.Web.WEB_KEYPAD_DISPLAY_TICKET_CONTROL_CHANGE_PRICE_BTN()} src={Images.MoneyEdit} direction={'column'} handleButton={onApplyLinePrice} />
			</Box>
			<Box gridRow={5} gridColumn={4}>
				<FunctionButton text={Res.Web.WEB_KEYPAD_DISPLAY_TICKET_CONTROL_CHANGE_QTY_BTN()} src={Images.EditQuantity} direction={'column'} handleButton={onApplyLineQuantity} />
			</Box>
		</Grid>
	);
};

export default Keypad;
